// The LLM-facing s-expression DSL reference.
//
// `<ExprDslReference>` is the canonical, self-contained single source of
// truth: one syntax primer, one op table per dispatch scope (every op
// emitted exactly once, deduped by name across the whole scope graph),
// and one enum-domain glossary. It is mounted ONCE in the system prompt
// — tools no longer re-embed the grammar; they rely on this one
// block. Rendering is composed entirely from
// the dedicated components in `components.tsx` so every doc surface is
// byte-identical.

import { Fragment, type ReactNode } from 'react';

import { canonicalDslRoots, walkOpMeta } from '../op-surface.ts';
import { dashboardEnumDomains, enumDomainNameOf } from '../ops/dashboard/enums.ts';
import { EnumDomains, OpDocTable } from './components.tsx';
import { entryOf, type OpDocScopeView } from './op-entry.ts';

export type { EnumDomainDoc } from './types.ts';

/**
 * The canonical DSL reference. Self-contained — no props. Mounted once
 * in the system prompt as an `<Instructions>` block; tools no longer
 * re-embed the grammar — this is the only copy.
 */
export function ExprDslReference(): ReactNode {
  const { views } = walkOpMeta(canonicalDslRoots).reduce<{
    readonly seen: ReadonlySet<string>;
    readonly views: readonly OpDocScopeView[];
  }>(
    (acc, { scope, entries }) => {
      const docEntries = entries.map(({ op, meta }) => entryOf(op, meta, enumDomainNameOf));
      const fresh = docEntries.filter(
        (e, i) => !acc.seen.has(e.name) && docEntries.findIndex((x) => x.name === e.name) === i,
      );
      if (fresh.length === 0) {
        return acc;
      }
      return {
        seen: new Set([...acc.seen, ...fresh.map((e) => e.name)]),
        views: [...acc.views, { name: scope.name, description: scope.description, entries: fresh }],
      };
    },
    { seen: new Set<string>(), views: [] },
  );

  return (
    <>
      <h1>S-expression DSL reference</h1>
      <p>
        Programs are nested s-expression calls. The sections below give the surface syntax, then one op table per
        dispatch scope — the blurb under each scope heading says where its ops are reachable. Each op lists its call
        signature, the meaning of every keyword argument, and a worked example. Enum-typed kwargs name a domain (e.g.{' '}
        <code>Color</code>); the legal values are in the final glossary.
      </p>
      <h2>Syntax</h2>
      <p>
        Every program is a tree of nested calls in s-expression form:{' '}
        <code>(op positional… :kwarg value … child child …)</code>. Positional arguments come first, then keyword
        arguments (<code>:name value</code> pairs), then any child forms.
      </p>
      <p>Argument values can be:</p>
      <ul>
        <li>
          bare symbols — e.g. <code>kpi</code>, <code>compact</code>, <code>sales</code>
        </li>
        <li>
          quoted strings — e.g. <code>"Revenue"</code> (use quotes when the value has spaces or punctuation)
        </li>
        <li>
          numbers — e.g. <code>4</code>, <code>1.5</code>, <code>-10</code>
        </li>
        <li>
          booleans — <code>true</code> / <code>false</code>
        </li>
        <li>
          vectors in square brackets — e.g. <code>[4 2]</code>, <code>[name (desc createdTime)]</code>
        </li>
        <li>
          nested forms — another <code>(op …)</code> call
        </li>
      </ul>
      {/* <p> */}
      {/*   Bare symbols and quoted strings are interchangeable for any scalar kwarg value — quote only when needed. Vectors */}
      {/*   are data tuples, not calls (distinct from forms). */}
      {/* </p> */}
      <h2>Column references</h2>
      <p>
        Reference a column of a dataset with the polymorphic <code>(col …)</code> op:
      </p>
      <ul>
        <li>
          <code>(col &lt;binding&gt; &lt;column&gt;)</code> — by bound dataset name (the usual case; the name comes from
          a prior tool's <code>outputDataset</code>)
        </li>
        <li>
          <code>(col &lt;dataset-expr&gt; &lt;column&gt;)</code> — by an inline expression that evaluates to a Dataset
        </li>
        <li>
          <code>(col &lt;binding&gt; &lt;column&gt; &lt;path&gt; …)</code> — extra path segments walk into nested struct
          columns
        </li>
      </ul>
      <p>
        A <code>TimeSeries</code> ref column is a <em>nested dataset of raw datapoints</em>, with inner columns{' '}
        <code>timestamp</code>, <code>value</code>, <code>isString</code>, and <code>unit</code>. There are two ways to
        consume it: <code>(unnest [tsCol] :pick [timestamp value] :as [ts val])</code> flattens it to raw, unaggregated
        per-sample rows (use this for raw datapoints); <code>(time-bucket [tsCol] :period day :agg […])</code>{' '}
        aggregates it into time buckets. <code>time-bucket</code> always aggregates — it cannot return raw samples.
      </p>
      <p>
        Every query result carries scalar <code>externalId</code> and <code>space</code> identity columns plus its
        declared view properties. To filter one query by ids another query produced, bind the first to a dataset and
        reference its column inside the second's <code>:where</code> — e.g.{' '}
        <code>(in externalId (col eyes externalId))</code>. To filter by a direct relation, target the relation's{' '}
        <code>externalId</code> subpath: <code>(in (col ds relCol externalId) (col other-ds externalId))</code>. Negate
        with <code>(not (in …))</code>.
        {/* The referenced column is materialised and its values inlined, then pushed to CDF */}
        {/* (a scalar id list as an <code>in</code> filter; a relation subpath as a <code>nested</code> filter) — no join */}
        {/* needed. */}
      </p>
      {/* <p> */}
      {/*   Single-space contract: a cross-dataset set reference is matched by <code>externalId</code> alone, so the */}
      {/*   producing and consuming queries must resolve to the same space. The referenced column must also yield a bounded */}
      {/*   number of distinct values; narrow the upstream query if it is too large or empty (an empty referenced set is a */}
      {/*   hard error). */}
      {/* </p> */}
      {views.map((v) => (
        <Fragment key={v.name}>
          <OpDocTable description={v.description} entries={v.entries} name={v.name} />
        </Fragment>
      ))}
      <EnumDomains domains={dashboardEnumDomains()} />
    </>
  );
}
